// src/routes/AppRouter.jsx
import React from 'react';
// Importamos 'useSearchParams'
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

// Layouts
import DashboardLayout from '../layouts/DashboardLayout';

import Loader from '../components/Loader';

// Vistas del Dashboard (Protegidas)
import DashboardView from '../views/DashboardView';
import XCoinStatusView from '../views/XCoinStatusView';
import ComprarVenderView from '../views/ComprarVenderView';
import BalanceView from '../views/BalanceView';
import BonosView from '../views/BonosView';
import DepositarRetirarView from '../views/DepositarRetirarView';

import ProfileView from '../views/ProfileView';
import TradingAutomatizadoView from '../views/TradingAutomatizadoView';
import StakingView from '../views/StakingView';
import AdminView from '../views/AdminView';
import ReferralTermsView from '../views/ReferralTermsView';
import DiagnosticsView from '../views/DiagnosticsView';

// Vistas de Autenticación (Públicas)
import LoginPage from '../views/LoginPage';
import RegisterPage from '../views/RegisterPage';
import PrivacyPolicy from '../views/PrivacyPolicy';
import VerifyEmailView from '../views/VerifyEmailView';
import ReferralLandingView from '../views/ReferralLandingView';


// Vista genérica
const PlaceholderView = ({ title }) => (
  <div style={{ padding: '20px' }}>
    <h1>{title}</h1>
    <p>Esta es una página de marcador de posición.</p>
  </div>
);

// --- COMPONENTE DE RUTA PROTEGIDA (Para el Dashboard con Layout) ---
const ProtectedRoute = () => {
  const { userData, loading } = useUser();

  if (loading) {
    return <Loader />;
  }

  if (!userData) {
    return <Navigate to="/login" replace />;
  }

  // --- NUEVA VALIDACIÓN: Correo Verificado ---
  if (!userData.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Esta ruta renderiza el Layout principal del dashboard
  return <DashboardLayout />;
};

// --- ¡COMPONENTE DE RUTA PROTEGIDA (Para el Modal)! ---
// Esta ruta es para el iframe: solo valida al usuario, NO muestra el layout.
const ModalProtectedRoute = ({ children }) => {
  const { userData, loading } = useUser();

  if (loading) {
    return <Loader />;
  }

  if (!userData) {

    return <Navigate to="/login?modal=true" replace />;
  }


  return children;
};



// --- ¡COMPONENTE DE RUTA PÚBLICA (Login y Registro)! ---
const PublicRoute = ({ children }) => {
  const { userData, loading } = useUser();

  // 1. Leemos los parámetros de la URL
  const [searchParams] = useSearchParams();
  const isModal = searchParams.get('modal') === 'true';

  if (loading) {
    return <Loader />;
  }

  // 2. ¡LÓGICA CORREGIDA Y MEJORADA!
  // Solo redirige si hay un usuario Y NO estamos en modo modal.
  if (userData && !isModal) {
    const redirectTarget = searchParams.get('redirect');

    // Si ya está logueado...
    // ...pero NO verificado, deja que entre (o redirige a /verify-email si prefiere)
    if (!userData.emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }

    if (redirectTarget === 'trading') {
      return <Navigate to="/trading-automatizado" replace />;
    }

    // Si queremos redirigir a 'comprar' específicamente
    if (redirectTarget === 'comprar') {
      return <Navigate to="/mercado/comprar-vender" replace />;
    }

    return <Navigate to="/" replace />;
  }

  // Si no hay usuario, O si estamos en modo modal, muestra la página (LoginPage)
  return children;
};

const AppRouter = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* --- RUTAS PÚBLICAS (Login y Registro) --- */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/politica-privacidad" element={<PrivacyPolicy />} />

        {/* --- RUTA DE VERIFICACIÓN (Semi-protegida) --- */}
        <Route path="/verify-email" element={
          <VerifyEmailView /> // Podríamos envolverla en un wrapper que chequee si hay usuario, pero si no hay, la vista maneja "No sesión"
        } />

        {/* --- LANDING DE REFERIDOS (Pública) --- */}
        <Route path="/invitacion" element={<ReferralLandingView />} />




        {/* --- RUTAS PROTEGIDAS (Todo el Dashboard CON Layout) --- */}
        {/* Todas las rutas aquí adentro se renderizarán DENTRO de <DashboardLayout> */}
        <Route path="/" element={<ProtectedRoute />}>
          <Route index element={<DashboardView />} />
          <Route path="mercado/comprar-vender" element={<ComprarVenderView />} />
          <Route path="mercado/estado-xcoin" element={<XCoinStatusView />} />
          <Route path="billetera/balance" element={<BalanceView />} />
          <Route path="billetera/bonos" element={<BonosView />} />
          <Route path="billetera/depositar-retirar" element={<DepositarRetirarView />} />

          <Route path="trading-automatizado" element={<TradingAutomatizadoView />} />
          <Route path="inversiones" element={<StakingView />} />
          <Route path="admin" element={<AdminView />} />
          <Route path="noticias" element={<PlaceholderView title="Noticias" />} />
          <Route path="referidos-info" element={<ReferralTermsView />} />
          <Route path="perfil" element={<ProfileView />} />
          <Route path="diagnostics" element={<DiagnosticsView />} />
          <Route path="*" element={<PlaceholderView title="404 - Página no encontrada" />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;